import Alert from '@mui/material/Alert';
import React, { Reducer, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import Table from 'components/shared/Table';
import { EMPTY_STRING_VALUE, getEmailLink, getUserFullName } from 'util/Format';
import { SortConfig, TableColumn } from 'store/types/Table';
import { userFullNameSortLabel } from 'util/Table';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';
import CompanyUser from 'store/types/CompanyUser';
import UseRequestData from 'store/types/UseRequestData';
import { Grid, Button, Checkbox, Skeleton } from '@mui/material';
import CompanyService from 'services/api/CompanyService';
import DirectoryListingService from 'services/api/DirectoryListingService';
import { useSnackbar } from 'notistack';
import reducer, {
  initialState,
  CompanyUsersTableAction,
  CompanyUsersTableActionType,
  CompanyUsersTableState,
  TableCompanyUsers,
} from './CompanyUsersTableReducer';
import PortalRoleChips from '../PortalRoleChips';
import DirectoryStatusChip from '../DirectoryStatusChip';
import DirectoryListing, { DirectoryListings } from 'store/types/DirectoryListing';
import JobFunction from 'store/types/JobFunction';
import EditCompanyUserModal from 'components/company/CompanyUsersSection/EditCompanyUserModal';
import { UserEmployerAccessRequestsData } from 'store/types/UserEmployerAccessRequest';
import { isPendingRequestToCompanyExist } from 'util/Company';

import commonStyles from 'styles/common.module.scss';

interface CompanyUsersTableProps extends UseRequestData<CompanyUser[]> {
  companyId: string;
  jobFunctionsList: JobFunction[];
  companyRequests?: UserEmployerAccessRequestsData;
  directoryListings?: DirectoryListings;
  showDirectoryStatus: boolean;
  refetchDirectoryListings: () => void;
}

const CompanyUsersTable: React.FunctionComponent<CompanyUsersTableProps> = ({
  companyId,
  data,
  loading,
  error,
  refetch,
  jobFunctionsList,
  companyRequests,
  directoryListings,
  showDirectoryStatus,
  refetchDirectoryListings,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loadingEmployeeUpdate, setLoadingEmployeeUpdate] = useState<boolean>(false);
  const [submittingDirectoryUserId, setSubmittingDirectoryUserId] = useState<string>();

  const [{ sort, list = [], selectedUser }, dispatch] = useReducer<
    Reducer<CompanyUsersTableState, CompanyUsersTableAction>
  >(reducer, initialState);

  const directoryListingByUserId = useMemo(() => {
    const result = new Map<string, DirectoryListing>();

    for (const network of directoryListings?.directoryNetworks ?? []) {
      for (const listing of network.listings ?? []) {
        if (!result.has(listing.userId)) {
          result.set(listing.userId, listing);
        }
      }
    }

    return result;
  }, [directoryListings]);

  useEffect(() => {
    if (data) {
      dispatch({
        type: CompanyUsersTableActionType.SetInitialList,
        payload: { initialList: data },
      });
    }
  }, [data, companyId]);

  const handleViewModalOpen = useCallback(
    (record) => () => {
      dispatch({
        type: CompanyUsersTableActionType.OpenViewUserModal,
        payload: { selectedUser: record },
      });
    },
    []
  );

  const handleViewModalClose = useCallback(() => {
    dispatch({ type: CompanyUsersTableActionType.CloseViewUserModal, payload: {} });
  }, []);

  const handleSortChange = useCallback((newSort: SortConfig) => {
    dispatch({
      type: CompanyUsersTableActionType.UpdateSort,
      payload: { sort: newSort },
    });
  }, []);

  const handleEmployeeToggleClick = useCallback(
    (record: TableCompanyUsers) => async () => {
      const { isEmployee, id } = record;

      setLoadingEmployeeUpdate(true);

      try {
        if (isEmployee) {
          await CompanyService.removeCompanyEmployee(companyId, id);

          enqueueSnackbar('Employee status successfully updated', {
            variant: 'success',
          });
        } else {
          await CompanyService.linkCompanyEmployee(companyId, id);

          enqueueSnackbar('Employee request created', {
            variant: 'success',
          });
        }

        await refetch();
      } catch (errorMessage) {
        enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
      } finally {
        setLoadingEmployeeUpdate(false);
      }
    },
    [companyId, enqueueSnackbar, refetch]
  );

  const handleSubmitForDirectory = useCallback(
    (record: TableCompanyUsers) => async () => {
      setSubmittingDirectoryUserId(record.id);

      try {
        await DirectoryListingService.createDirectoryListings(companyId, record.id);

        await refetchDirectoryListings();

        enqueueSnackbar('Person successfully submitted for directory review', {
          variant: 'success',
        });
      } catch (errorMessage) {
        enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
      } finally {
        setSubmittingDirectoryUserId(undefined);
      }
    },
    [companyId, enqueueSnackbar, refetchDirectoryListings]
  );

  const renderDirectoryStatus = useCallback(
    (record: TableCompanyUsers) => {
      const listing = directoryListingByUserId.get(String(record.id));

      if (!listing) {
        const submitting = submittingDirectoryUserId === record.id;

        return (
          <Button
            size={'small'}
            color={'secondary'}
            variant={'outlined'}
            disabled={Boolean(submittingDirectoryUserId)}
            onClick={handleSubmitForDirectory(record)}
            sx={{ width: '100%' }}
          >
            {submitting ? 'Submitting...' : 'Submit for Directory'}
          </Button>
        );
      }

      return listing.directoryStatusName?.trim() ? (
        <DirectoryStatusChip status={listing.directoryStatusName} />
      ) : (
        EMPTY_STRING_VALUE
      );
    },
    [directoryListingByUserId, handleSubmitForDirectory, submittingDirectoryUserId]
  );

  const columns: Array<TableColumn<TableCompanyUsers>> = [
    {
      dataIndex: 'isEmployee',
      label: 'Employee',
      sortable: true,
      render: (isEmployee: boolean, record: TableCompanyUsers) => {
        const pending = companyRequests && isPendingRequestToCompanyExist(record.id, companyRequests);

        return loadingEmployeeUpdate ? (
          <Skeleton variant={'rectangular'} width={45} />
        ) : (
          <Checkbox
            color={'primary'}
            checked={isEmployee}
            indeterminate={pending}
            disabled={pending}
            onClick={handleEmployeeToggleClick(record)}
          />
        );
      },
    },
    {
      dataIndex: userFullNameSortLabel,
      label: 'Name',
      sortable: true,
      render: (_: any, record: TableCompanyUsers) => getUserFullName(record, true),
    },
    {
      dataIndex: 'title',
      label: 'Job title',
      sortable: true,
      render: (title: string) => (title ? title : EMPTY_STRING_VALUE),
    },
    {
      dataIndex: 'email',
      label: 'Email',
      sortable: true,
      render: (email: string) => (email ? getEmailLink(email) : EMPTY_STRING_VALUE),
    },
    {
      dataIndex: 'functions',
      label: 'Portal Role',
      sortable: true,
      render: (functions: CompanyUser['functions']) => <PortalRoleChips functions={functions} />,
    },
    ...(showDirectoryStatus
      ? [
          {
            key: 'directory-status',
            label: 'Directory Status',
            align: 'center' as const,
            render: (_: unknown, record: TableCompanyUsers) => renderDirectoryStatus(record),
          },
        ]
      : []),
    {
      key: 'actions',
      label: 'Action',
      align: 'center',
      render: (_: any, record: TableCompanyUsers) => (
        <Button size={'small'} onClick={handleViewModalOpen(record)} variant={'outlined'}>
          {'Edit'}
        </Button>
      ),
    },
  ];

  return (
    <>
      <Grid {...defaultGridContainerProps}>
        {error ? (
          <Grid {...defaultGridItemProps}>
            <Alert severity={'error'} className={commonStyles.alert}>
              {error}
            </Alert>
          </Grid>
        ) : (
          <Grid {...defaultGridItemProps}>
            <Table
              columns={columns}
              list={list}
              sort={sort}
              onSortChange={handleSortChange}
              loading={loading}
              showPagination={true}
            />
          </Grid>
        )}
      </Grid>
      {selectedUser && (
        <EditCompanyUserModal
          open={!!selectedUser}
          onClose={handleViewModalClose}
          data={selectedUser}
          jobFunctionsList={jobFunctionsList}
          refetch={refetch}
          companyId={companyId}
        />
      )}
    </>
  );
};
export default CompanyUsersTable;
